import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  classifyServerSearchPayload,
  createSuccessResult,
  errors,
  IApiError,
  IPassengerDto,
  IPassengerStationLocationRequestDto,
  IPassengerStationLocationResponseDto,
  ISearchCriteria,
  Log,
  normalizeErrors,
  PassengerApi,
  PassengerEntity,
  SimpleResult,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export const usePassengersActions = () => {
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setLoading = useSetRecoilState(apiDataFetchingAtom);
  const { t } = useTranslation();

  const searchPassengerStationsLocations = useEvent(
    async (
      payload: IPassengerStationLocationRequestDto,
    ): Promise<SimpleResult<IPassengerStationLocationResponseDto>> => {
      // Intentionally no apiDataFetchingAtom here: the fullscreen LoaderMui dimmed the bookings calendar behind the drawer.
      try {
        const stationsLocationsResult =
          await PassengerApi.searchPassengerStationsLocations(payload);

        if (
          !stationsLocationsResult?.success ||
          !stationsLocationsResult.data
        ) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        Log.debug(
          "Search Passenger Stations Locations dto received: ",
          stationsLocationsResult.data,
        );

        return createSuccessResult(stationsLocationsResult.data);
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      }
    },
  );

  const searchPassengerByCriteria = useEvent(
    async (
      payload: ISearchCriteria<PassengerEntity>,
    ): Promise<SimpleResult<IPassengerDto[]>> => {
      try {
        setLoading(true);
        const result = await PassengerApi.searchPassengerByCriteria(payload);

        if (!result?.success || !result.data) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        const parsed = classifyServerSearchPayload(result.data);

        if (!parsed) {
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        const rows =
          parsed.kind === "groups"
            ? parsed.groups.flatMap(group => group.groupData ?? [])
            : parsed.items;

        Log.debug("Search Passenger by criteria dto received: ", rows);

        return createSuccessResult(rows as IPassengerDto[]);
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setLoading(false);
      }
    },
  );

  const recalculateWalkingDistance = useEvent(
    async (passengerId: number): Promise<SimpleResult<void>> => {
      try {
        setLoading(true);
        const result =
          await PassengerApi.recalculateWalkingDistance(passengerId);

        if (!result?.success) {
          warnAndToastSimpleResult(
            result ??
              new SimpleResult({ error: t(errors.common.emptyApiResponse) }),
            t,
          );
          return new SimpleResult({
            error: t(errors.common.emptyApiResponse),
          });
        }

        Log.infoAndToast(
          t("general.ENTITY_SUCCESSFULLY_UPDATED", {
            entity: t("passenger.single"),
          }),
        );
        return createSuccessResult();
      } catch (error) {
        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setLoading(false);
      }
    },
  );

  return {
    searchPassengerStationsLocations,
    searchPassengerByCriteria,
    recalculateWalkingDistance,
  };
};
